import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, Layers, UploadCloud, CheckCircle2, Wallet, User, Inbox, Download, Loader2, ListChecks, ChevronDown, ChevronUp, Zap, PauseCircle, FileUp, RotateCcw, Hourglass, MessageSquare, LayoutDashboard, LayoutPanelLeft, BookOpenCheck, Eye, History } from "lucide-react";
import { VideoPlayer, fmtTime } from "@/components/dashboard/VideoPlayer";
import { AudioPlayer } from "@/components/dashboard/AudioPlayer";
import { WorkerMessages } from "@/components/dashboard/WorkerMessages";
import { AnnouncementBanner } from "@/components/dashboard/Announcement";
import { ChangePasswordCard } from "@/components/dashboard/ChangePasswordCard";
import { NotificationPrefsCard } from "@/components/dashboard/NotificationPrefsCard";
import { ProofModal } from "@/components/admin/ProofModal";
import { ManualsPage } from "@/components/manuals/ManualsPage";
import { PaymentDetailsForm } from "@/components/dashboard/PaymentDetailsForm";

const isVideo = (name = "") => /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(name);
import { PrivateHeader } from "@/components/dashboard/PrivateHeader";
import { API, authHeaders, fileUrl, ROLE_BADGE_STYLES, JOB_STATUS_STYLES, JOB_STATUS_LABELS } from "@/constants/roles";

const TABS = [
  { id: "available", label: "Available Jobs", icon: Briefcase },
  { id: "my-jobs", label: "My Jobs", icon: Layers },
  { id: "submissions", label: "Submissions", icon: UploadCloud },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "earnings", label: "Earnings", icon: Wallet },
  { id: "profile", label: "Profile", icon: User },
];

const SUB_STATUS_STYLES = {
  awaiting_review: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  manager_approved: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  revision_requested: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  superseded: "bg-[var(--pv-subtle)] text-[var(--pv-text-4)] border-[var(--pv-border)]",
};

const EmptyState = ({ title, text, testid }) => (
  <div data-testid={testid} className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16 text-center">
    <Inbox className="h-10 w-10 text-[var(--pv-text-2)]" />
    <p className="font-display text-base font-bold text-[var(--pv-text)]">{title}</p>
    <p className="max-w-md px-6 text-sm text-[var(--pv-text-4)]">{text}</p>
  </div>
);

const JobDetails = ({ job }) => (
  <div className="mt-5 space-y-4 rounded-2xl bg-[var(--pv-subtle)] p-5 text-sm">
    {job.description && <div><p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Description</p><p className="mt-1 whitespace-pre-wrap text-[var(--pv-text-2)]">{job.description}</p></div>}
    {job.script && <div><p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Script</p><p className="mt-1 max-h-60 overflow-y-auto whitespace-pre-wrap rounded-xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4 text-[var(--pv-text-2)]">{job.script}</p></div>}
    {job.instructions && <div><p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Instructions</p><p className="mt-1 whitespace-pre-wrap text-[var(--pv-text-2)]">{job.instructions}</p></div>}
    {job.assets && <div><p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Assets & links</p><p className="mt-1 whitespace-pre-wrap text-[var(--pv-text-2)]">{job.assets}</p></div>}
    {job.references?.length > 0 && (
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">References (match this quality)</p>
        <ul className="mt-1 space-y-1">
          {job.references.map((r, i) => (
            <li key={i}><a href={r} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-cyan-400 hover:text-cyan-300">{r}</a></li>
          ))}
        </ul>
      </div>
    )}
    {(job.audio_path || job.additional_files?.length > 0) && (
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Files</p>
        {job.audio_path && (
          <div className="mt-2">
            <AudioPlayer path={job.audio_path} filename="Job audio (voice + music)" testid={`job-audio-player-${job.id}`} />
          </div>
        )}
        {job.additional_files?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {job.additional_files?.map((p, i) => (
              <a key={i} data-testid={`job-file-dl-${job.id}-${i}`} href={fileUrl(p)} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-raised)] px-3 py-1.5 text-xs font-bold text-[var(--pv-text-2)] hover:bg-[var(--pv-raised)]">
                <Download className="h-3 w-3" /> File {i + 1}
              </a>
            ))}
          </div>
        )}
      </div>
    )}
  </div>
);

const SubmitWorkForm = ({ job, onDone }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose your finished video file first.");
      return;
    }
    if (file.size > 250 * 1024 * 1024) {
      toast.error(`That file is ${(file.size / (1024 * 1024)).toFixed(0)} MB — the maximum is 250 MB.`);
      return;
    }
    setSending(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("note", note);
      await axios.post(`${API}/jobs/${job.id}/submit`, form, { headers: authHeaders() });
      toast.success("Work submitted — it's now in review.");
      setFile(null);
      setNote("");
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid={`submit-form-${job.id}`} onSubmit={submit} className="mt-4 rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-5">
      <p className="text-sm font-bold text-[var(--pv-text)]">
        {job.status === "in_revision" ? "Upload Revised Version" : job.status === "awaiting_review" ? "Replace / update your submission" : "Submit your finished work"}
      </p>
      {job.status === "awaiting_review" && (
        <p className="mt-1 text-[11px] font-medium text-[var(--pv-text-4)]">
          Your current file stays in the version history — the new upload becomes the version under review. Pay and deadline are unchanged.
        </p>
      )}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label data-testid={`submit-file-${job.id}`} className="flex cursor-pointer items-center gap-2 rounded-2xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] px-4 py-3 text-xs font-semibold text-[var(--pv-text-4)] transition-colors hover:border-cyan-500/30">
          <FileUp className="h-4 w-4 shrink-0 text-cyan-500" />
          <span className="truncate">{file ? `${file.name} (${(file.size / (1024 * 1024)).toFixed(0)} MB)` : "Choose video file (max 250MB)"}</span>
          <input type="file" accept="video/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <input
          data-testid={`submit-note-${job.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={job.status === "active" ? "Note for review (optional)" : "What changed? (e.g. re-rendered in 3:4)"}
          className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
        />
      </div>
      <button
        data-testid={`submit-work-btn-${job.id}`}
        type="submit"
        disabled={sending}
        className="mt-3 flex items-center gap-2 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white shadow-md shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-60"
      >
        {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {sending ? "Uploading..." : job.status === "active" ? "Submit for review" : "Submit new version"}
      </button>
    </form>
  );
};

// Every upload for a job is kept — newest is marked Current, older ones stay as history.
const VERSION_CHIP = {
  awaiting_review: { label: "In review", cls: "border-amber-500/30 bg-amber-500/15 text-amber-300" },
  manager_approved: { label: "Quality approved", cls: "border-violet-500/30 bg-violet-500/15 text-violet-300" },
  revision_requested: { label: "Sent back for revision", cls: "border-rose-500/30 bg-rose-500/15 text-rose-300" },
  superseded: { label: "Superseded", cls: "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-4)]" },
  approved: { label: "Approved", cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300" },
};

export const VersionHistory = ({ job, canDownload = false }) => {
  const [open, setOpen] = useState(false);
  const subs = [...(job.submissions || [])].sort((a, b) => b.version - a.version);
  if (subs.length < 2) return null;
  return (
    <div data-testid={`version-history-${job.id}`} className="mt-4 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
      <button
        data-testid={`version-history-toggle-${job.id}`}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--pv-text-3)] transition-colors hover:text-cyan-400"
      >
        <History className="h-3.5 w-3.5" /> Version history ({subs.length}) {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <ul className="mt-3 space-y-2">
          {subs.map((s, i) => {
            const chip = VERSION_CHIP[s.status] || VERSION_CHIP.superseded;
            return (
              <li key={s.id} data-testid={`version-row-${s.id}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2.5">
                <span className="rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-raised)] px-2 py-0.5 text-[10px] font-bold text-[var(--pv-text-2)]">v{s.version}</span>
                <span className="text-xs font-bold text-[var(--pv-text-2)]">{s.version === 1 ? "Original submission" : "Revised submission"}</span>
                {i === 0 && (
                  <span data-testid={`version-current-${s.id}`} className="rounded-full border border-cyan-500/30 bg-cyan-500/15 px-2 py-0.5 text-[9px] font-bold uppercase text-cyan-300">Current — latest</span>
                )}
                <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase ${chip.cls}`}>{chip.label}</span>
                <span className="text-[10px] font-medium text-[var(--pv-text-4)]">
                  {new Date(s.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
                {s.note && <span className="w-full text-[11px] italic text-[var(--pv-text-4)] sm:w-auto sm:flex-1">“{s.note}”</span>}
                {canDownload && (
                  <a data-testid={`version-download-${s.id}`} href={fileUrl(s.file_path)} download={s.filename} className="ml-auto flex items-center gap-1 text-[11px] font-bold text-cyan-400 hover:text-cyan-300">
                    <Download className="h-3 w-3" /> {s.filename}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export const WorkerDashboard = ({ me, theme, onToggleTheme, embedded = false }) => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    // deep links like /dashboard?tab=available (e.g. from a job-alert notification or email)
    const t = searchParams.get("tab");
    return TABS.some((x) => x.id === t) ? t : "available";
  });
  const [available, setAvailable] = useState(null);
  const [paused, setPaused] = useState(false);
  const [myJobs, setMyJobs] = useState(null);
  const [payments, setPayments] = useState(null);
  const [proofFor, setProofFor] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [guideline, setGuideline] = useState(null);
  const [expanded, setExpanded] = useState(null);
  // deep link /dashboard?tab=my-jobs&job=<id> (e.g. from a job-nudge notification): open the job
  useEffect(() => {
    const j = searchParams.get("job");
    if (j && myJobs && myJobs.some((x) => x.id === j)) {
      setTab("my-jobs");
      setExpanded(j);
      setTimeout(() => {
        document.querySelector(`[data-testid="my-job-expand-${j}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myJobs]);
  const [openItem, setOpenItem] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [msgUnread, setMsgUnread] = useState(0);
  const [announcement, setAnnouncement] = useState(null);
  const [manualCount, setManualCount] = useState(0);
  const [paySetup, setPaySetup] = useState(null); // own payment details status + pending requests
  const [replaceOpen, setReplaceOpen] = useState(null); // job id with the replace-upload form open
  const [msgPreset, setMsgPreset] = useState(null); // {conversation, jobId} — "Ask a question" handoff
  const [layout, setLayout] = useState(() => me.worker_layout || localStorage.getItem("melto_layout") || "dashboard");
  const vrefs = useRef({});

  const changeLayout = (next) => {
    setLayout(next);
    localStorage.setItem("melto_layout", next);
    axios.patch(`${API}/my/layout`, { layout: next }, { headers: authHeaders() }).catch(() => {});
  };

  const refreshAnnouncement = useCallback(() => {
    axios
      .get(`${API}/announcement`, { headers: authHeaders() })
      .then((r) => setAnnouncement(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshAnnouncement();
    const t = setInterval(refreshAnnouncement, 20000);
    return () => clearInterval(t);
  }, [refreshAnnouncement]);

  const refreshMsgUnread = useCallback(() => {
    axios
      .get(`${API}/my/messages/unread`, { headers: authHeaders() })
      .then((r) => setMsgUnread(r.data.unread))
      .catch(() => {});
  }, []);

  // manuals shared with the worker role/account (drives the conditional Manuals tab)
  useEffect(() => {
    axios
      .get(`${API}/manuals`, { headers: authHeaders() })
      .then((r) => setManualCount((r.data || []).length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshMsgUnread();
    const t = setInterval(refreshMsgUnread, 20000);
    return () => clearInterval(t);
  }, [refreshMsgUnread]);

  const getRef = (id) => (vrefs.current[id] ||= { current: null });
  const seekTo = (id, t) => {
    const v = vrefs.current[id]?.current;
    if (v) {
      v.currentTime = t;
      v.play().catch(() => {});
    }
  };

  const loadAvailable = useCallback(() => {
    axios
      .get(`${API}/jobs/available`, { headers: authHeaders() })
      .then((r) => {
        setAvailable(r.data.jobs);
        setPaused(r.data.paused);
      })
      .catch(() => toast.error("Could not load available jobs."));
  }, []);

  const loadMyJobs = useCallback(() => {
    axios
      .get(`${API}/my/jobs`, { headers: authHeaders() })
      .then((r) => setMyJobs(r.data))
      .catch(() => toast.error("Could not load your jobs."));
  }, []);

  const loadPayments = useCallback(() => {
    axios
      .get(`${API}/my/payments`, { headers: authHeaders() })
      .then((r) => setPayments(r.data))
      .catch(() => toast.error("Could not load earnings."));
  }, []);

  const loadOnboarding = useCallback(() => {
    axios
      .get(`${API}/my/onboarding`, { headers: authHeaders() })
      .then((r) => setOnboarding(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadAvailable();
    loadMyJobs();
    loadPayments();
    loadOnboarding();
    axios
      .get(`${API}/my/payment-details`, { headers: authHeaders() })
      .then((r) => setPaySetup(r.data))
      .catch(() => {});
    axios
      .get(`${API}/guidelines`, { headers: authHeaders() })
      .then((r) => {
        const mine = r.data.find((g) => g.role === me.worker_role);
        setGuideline(mine && mine.content && mine.content.trim() ? mine : null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAvailable, loadMyJobs, loadPayments, loadOnboarding]);

  // notification/email deep link: /dashboard?tab=profile&focus=payment → open the Payment Details form
  useEffect(() => {
    if (searchParams.get("focus") === "payment") {
      setTab("profile");
      setTimeout(() => document.getElementById("payment-details-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claim = async (jobId) => {
    setClaimingId(jobId);
    try {
      await axios.post(`${API}/jobs/${jobId}/claim`, {}, { headers: authHeaders() });
      toast.success("Job claimed — it's in My Jobs now. Good luck!");
      loadAvailable();
      loadMyJobs();
      setTab("my-jobs");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not claim this job.");
      loadAvailable();
    } finally {
      setClaimingId(null);
    }
  };

  const toggleItem = async (item) => {
    if (item.auto === "first_job") return;
    try {
      await axios.patch(`${API}/my/onboarding`, { item_id: item.id, done: !item.done }, { headers: authHeaders() });
      loadOnboarding();
    } catch {
      toast.error("Could not update checklist.");
    }
  };

  const activeJobs = useMemo(() => (myJobs || []).filter((j) => ["active", "awaiting_review", "in_revision"].includes(j.status)), [myJobs]);
  const allSubs = useMemo(
    () =>
      (myJobs || [])
        .flatMap((j) => (j.submissions || []).map((s) => ({ ...s, job_title: j.title, job_status: j.status })))
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [myJobs]
  );
  const revisionSubs = allSubs.filter((s) => s.status === "revision_requested");

  const totalPaid = (payments || []).filter((p) => p.status === "paid").reduce((s, p) => s + p.amount, 0);
  const totalOwed = (payments || []).filter((p) => p.status === "owed").reduce((s, p) => s + p.amount, 0);

  const onboardingDone = onboarding?.done;
  const checkedCount = onboarding?.items?.filter((i) => i.done).length || 0;

  const renderTabs = (vertical = false) => (
    <div data-testid="worker-tabs" className={vertical ? "flex flex-col gap-1.5" : "flex flex-wrap gap-2"}>
      {(manualCount > 0 ? [...TABS, { id: "manuals", label: "Manuals", icon: BookOpenCheck }] : TABS).map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          data-testid={`worker-tab-${id}`}
          onClick={() => setTab(id)}
          className={
            vertical
              ? `flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  tab === id ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)] hover:text-[var(--pv-text)]"
                }`
              : `flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                  tab === id
                    ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25"
                    : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)] hover:border-cyan-500/30"
                }`
          }
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
          {id === "messages" && msgUnread > 0 && (
            <span data-testid="worker-messages-badge" className="flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
              {msgUnread}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  const layoutToggle = (
    <div data-testid="worker-layout-toggle" className="flex w-fit shrink-0 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-1">
      {[
        ["dashboard", "Dashboard", LayoutDashboard],
        ["workspace", "Workspace", LayoutPanelLeft],
      ].map(([id, label, Icon]) => (
        <button
          key={id}
          data-testid={`layout-${id}-btn`}
          onClick={() => changeLayout(id)}
          className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${layout === id ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)]"}`}
        >
          <Icon className="h-3.5 w-3.5" /> {label}
        </button>
      ))}
    </div>
  );

  const workspaceStrip = (
    <div data-testid="worker-workspace-strip" className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-5 py-4">
      <p className="font-display text-base font-bold text-[var(--pv-text)]">{me.name || me.email}</p>
      {(me.worker_role || me.applied_role) && (
        <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${ROLE_BADGE_STYLES[me.worker_role || me.applied_role] || "bg-[var(--pv-subtle)] text-[var(--pv-text)] border-[var(--pv-border-strong)]"}`}>
          {me.worker_role || me.applied_role}
        </span>
      )}
      <span className="text-xs text-[var(--pv-text-4)]">Active jobs: <span className="font-bold text-[var(--pv-text)]">{activeJobs.length}</span></span>
      <span className="text-xs text-[var(--pv-text-4)]">Owed: <span className="font-bold text-amber-400">${totalOwed.toFixed(2)}</span></span>
      <span className="text-xs text-[var(--pv-text-4)]">Earned: <span className="font-bold text-emerald-400">${totalPaid.toFixed(2)}</span></span>
    </div>
  );

  return (
    <div data-testid="worker-dashboard" className={embedded ? "text-[var(--pv-text-2)]" : "min-h-screen bg-[var(--pv-bg)] text-[var(--pv-text-2)]"}>
      {!embedded && (
        <PrivateHeader
          title="Production"
          theme={theme}
          onToggleTheme={onToggleTheme}
          badge={<span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase text-emerald-300">Active worker</span>}
        />
      )}
      <main className={embedded ? "" : `mx-auto px-4 sm:px-6 ${layout === "workspace" ? "max-w-7xl py-6" : "max-w-6xl py-10"}`}>
        {announcement?.enabled && announcement.text && (
          <div className="mb-6">
            <AnnouncementBanner text={announcement.text} />
          </div>
        )}
        {layout === "dashboard" && (
        <div className="rounded-3xl border border-[var(--pv-border)] bg-gradient-to-br from-cyan-500 to-cyan-600 p-8 text-[var(--pv-text)]  sm:p-10">
          <p className="font-mono-label text-[10px] font-bold uppercase tracking-widest text-cyan-100">Welcome to Melto</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h1 data-testid="worker-welcome" className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
              {me.name || me.email}
            </h1>
            {(me.display_role || me.worker_role || me.applied_role) && (
              <span className={`rounded-full border px-3 py-1 text-xs font-bold ${ROLE_BADGE_STYLES[me.display_role] || ROLE_BADGE_STYLES[me.worker_role || me.applied_role] || "bg-[var(--pv-subtle)] text-[var(--pv-text)] border-[var(--pv-border-strong)]"}`}>
                {me.display_role || me.worker_role || me.applied_role}
              </span>
            )}
          </div>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-cyan-50">
            Claim a job, deliver great work before the deadline, and get paid per approved job — exact pay is shown upfront on every card. No fixed schedule.
          </p>
        </div>
        )}

        {layout === "workspace" && workspaceStrip}
        <div className={layout === "workspace" ? "mt-6 md:flex md:items-start md:gap-6" : ""}>
          {layout === "workspace" && (
            <aside data-testid="worker-workspace-nav" className="mb-6 shrink-0 md:sticky md:top-6 md:mb-0 md:w-60">
              {renderTabs(true)}
              <div className="mt-4">{layoutToggle}</div>
            </aside>
          )}
          <div className="min-w-0 flex-1">

        {paySetup && (paySetup.pending_requests || []).length > 0 && (
          <div data-testid="payment-required-banner" className="mt-6 rounded-3xl border border-amber-500/30 bg-amber-500/10 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Wallet className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                <div>
                  <h2 className="font-display text-base font-bold text-amber-200">Payment details required</h2>
                  <p className="mt-1 text-xs font-medium leading-relaxed text-amber-200/80">
                    Melto Studios needs your payment information before your payment can be sent.
                    {" "}Requested: <b>{paySetup.pending_requests.map((r) => ({ all: "primary + backup", primary: "primary method", backup: "backup method", replacement: "updated details" }[r.kind] || r.kind)).join(" + ")}</b>.
                  </p>
                </div>
              </div>
              <button
                data-testid="payment-required-cta"
                onClick={() => { setTab("profile"); setTimeout(() => document.getElementById("payment-details-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250); }}
                className="rounded-full bg-amber-500 px-5 py-2.5 text-xs font-bold text-amber-950 transition-colors hover:bg-amber-400"
              >
                Add payment details
              </button>
            </div>
          </div>
        )}

        {paySetup && !(paySetup.pending_requests || []).length && paySetup.status !== "complete" && (
          <div data-testid="payment-setup-banner" className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-cyan-500/20 bg-cyan-500/5 p-6">
            <div className="flex items-start gap-3">
              <Wallet className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" />
              <div>
                <h2 className="font-display text-base font-bold text-[var(--pv-text)]">Set up your payment details</h2>
                <p className="mt-1 text-xs font-medium leading-relaxed text-[var(--pv-text-4)]">
                  {paySetup.status === "missing"
                    ? "Tell us how to pay you — a primary method plus a backup keeps your payouts unblockable."
                    : "Almost there — part of your payment setup is incomplete."}
                </p>
              </div>
            </div>
            <button
              data-testid="payment-setup-cta"
              onClick={() => { setTab("profile"); setTimeout(() => document.getElementById("payment-details-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250); }}
              className="rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-cyan-600"
            >
              Set up payment details
            </button>
          </div>
        )}

        {onboarding && !onboardingDone && (
          <div data-testid="onboarding-card" className="mt-6 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-6  sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <ListChecks className="h-5 w-5 text-cyan-400" />
                <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Getting started</h2>
              </div>
              <span data-testid="onboarding-progress" className="rounded-full bg-cyan-500/10 border border-cyan-500/20 px-3 py-1 text-xs font-bold text-cyan-300">
                {checkedCount}/{onboarding.items.length} done
              </span>
            </div>
            <div className="mt-5 space-y-2.5">
              {onboarding.items.map((item) => (
                <div key={item.id} data-testid={`onboarding-item-${item.id}`} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)]">
                  <div className="flex items-center gap-3 px-4 py-3">
                    <button
                      data-testid={`onboarding-check-${item.id}`}
                      onClick={() => toggleItem(item)}
                      disabled={item.auto === "first_job"}
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        item.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-[var(--pv-border-strong)] bg-[var(--pv-card)] hover:border-cyan-400"
                      } ${item.auto === "first_job" ? "cursor-default" : "cursor-pointer"}`}
                      aria-label={item.done ? "Completed" : "Mark complete"}
                    >
                      {item.done && <CheckCircle2 className="h-4 w-4" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold ${item.done ? "text-[var(--pv-text-4)] line-through" : "text-[var(--pv-text)]"}`}>{item.title}</p>
                    </div>
                    {item.auto === "first_job" && (
                      <span className="flex shrink-0 items-center gap-1 rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                        <Zap className="h-3 w-3" /> Auto
                      </span>
                    )}
                    {item.body && (
                      <button
                        data-testid={`onboarding-expand-${item.id}`}
                        onClick={() => setOpenItem(openItem === item.id ? null : item.id)}
                        className="shrink-0 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-1.5 text-[var(--pv-text-4)] hover:border-cyan-500/30"
                        aria-label="Read more"
                      >
                        {openItem === item.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                  {openItem === item.id && item.body && (
                    <p data-testid={`onboarding-body-${item.id}`} className="whitespace-pre-wrap px-4 pb-4 pl-12 text-xs leading-relaxed text-[var(--pv-text-4)]">{item.body}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {guideline && (
          <div data-testid="worker-guidelines-card" className="mt-6 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-6 sm:p-8">
            <div className="flex items-center gap-2.5">
              <BookOpenCheck className="h-5 w-5 text-cyan-400" />
              <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">{guideline.role} guidelines</h2>
            </div>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[var(--pv-text-3)]">{guideline.content}</p>
          </div>
        )}
        {onboardingDone && (
          <div data-testid="onboarding-complete" className="mt-6 flex items-center gap-2.5 rounded-3xl border border-emerald-500/20 bg-emerald-500/10 px-6 py-4 text-sm font-semibold text-emerald-300">
            <CheckCircle2 className="h-5 w-5" /> Onboarding complete — you're fully crewed up.
          </div>
        )}

        {layout === "dashboard" && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            {renderTabs(false)}
            {layoutToggle}
          </div>
        )}

        <div className="mt-6">
          {tab === "available" && (
            <div data-testid="worker-available-panel" className="space-y-4">
              {paused && (
                <div data-testid="production-paused-notice" className="flex items-center gap-2.5 rounded-3xl border border-rose-500/20 bg-rose-500/10 px-6 py-4 text-sm font-semibold text-rose-300">
                  <PauseCircle className="h-5 w-5" /> Production is paused right now — no new jobs can be claimed. Check back soon.
                </div>
              )}
              {!available ? (
                <p data-testid="available-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading jobs...</p>
              ) : available.length === 0 ? (
                <EmptyState testid="available-empty" title="No available jobs right now" text="New jobs drop as production moves. Check back soon — when one fits your role, claim it before someone else does." />
              ) : (
                available.map((j) => (
                  <article key={j.id} data-testid={`available-job-${j.id}`} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2.5">
                          <h3 className="font-display text-lg font-semibold text-[var(--pv-text)]">{j.title}</h3>
                          <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${ROLE_BADGE_STYLES[j.role_required] || ""}`}>{j.role_required}</span>
                          {j.priority !== "normal" && (
                            <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${j.priority === "urgent" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>{j.priority}</span>
                          )}
                        </div>
                        <p className="mt-1.5 text-xs font-medium text-[var(--pv-text-4)]">
                          <span className="font-display text-base font-bold text-[var(--pv-text)]">${j.estimated_pay.toFixed(2)}</span>
                          {j.video_duration && <span className="ml-3">{j.video_duration}</span>}
                          {j.deadline
                            ? <span className="ml-3">Due {new Date(j.deadline + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                            : j.deadline_days
                              ? <span className="ml-3" data-testid={`days-after-claim-${j.id}`}>Deadline: {j.deadline_days} days after claim</span>
                              : null}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`available-expand-${j.id}`}
                          onClick={() => setExpanded(expanded === j.id ? null : j.id)}
                          className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-4)] hover:border-cyan-500/30"
                        >
                          {expanded === j.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          Details
                        </button>
                        <button
                          data-testid={`claim-btn-${j.id}`}
                          onClick={() => claim(j.id)}
                          disabled={paused || claimingId === j.id}
                          className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2 text-xs font-bold text-white shadow-md shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-50"
                        >
                          {claimingId === j.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                          Claim job
                        </button>
                      </div>
                    </div>
                    {j.description && <p className="mt-3 line-clamp-2 text-sm text-[var(--pv-text-4)]">{j.description}</p>}
                    {expanded === j.id && <JobDetails job={j} />}
                  </article>
                ))
              )}
            </div>
          )}

          {tab === "my-jobs" && (
            <div data-testid="worker-my-jobs-panel" className="space-y-4">
              {!myJobs ? (
                <p data-testid="my-jobs-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading your jobs...</p>
              ) : activeJobs.length === 0 ? (
                <EmptyState title="No active jobs" text="Claim a job from Available Jobs and it will show up here with everything you need to deliver." testid="my-jobs-empty" />
              ) : (
                activeJobs.map((j) => {
                  const latestRevision = (j.submissions || []).find((s) => s.status === "revision_requested");
                  return (
                    <article key={j.id} data-testid={`my-job-${j.id}`} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2.5">
                            <h3 className="font-display text-lg font-semibold text-[var(--pv-text)]">{j.title}</h3>
                            <span data-testid={`my-job-status-${j.id}`} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${JOB_STATUS_STYLES[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
                          </div>
                          <p className="mt-1.5 text-xs font-medium text-[var(--pv-text-4)]">
                            <span className="font-display text-base font-bold text-[var(--pv-text)]">${j.estimated_pay.toFixed(2)}</span>
                            {j.deadline
                            ? <span className="ml-3">Due {new Date(j.deadline + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                            : j.deadline_days
                              ? <span className="ml-3" data-testid={`days-after-claim-${j.id}`}>Deadline: {j.deadline_days} days after claim</span>
                              : null}
                          </p>
                        </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          data-testid={`ask-question-${j.id}`}
                          onClick={() => { setMsgPreset({ conversation: "pm_team", jobId: j.id }); setTab("messages"); }}
                          className="flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3.5 py-2 text-xs font-bold text-cyan-300 transition-colors hover:bg-cyan-500/20"
                        >
                          <MessageSquare className="h-3.5 w-3.5" /> Ask a question
                        </button>
                        <button
                          data-testid={`my-job-expand-${j.id}`}
                          onClick={() => setExpanded(expanded === j.id ? null : j.id)}
                          className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-4)] hover:border-cyan-500/30"
                        >
                          {expanded === j.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          Brief & files
                        </button>
                      </div>
                      </div>

                      {j.status === "in_revision" && latestRevision?.feedback && (
                        <div data-testid={`revision-feedback-panel-${j.id}`} className="mt-4 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
                          <p className="flex items-start gap-2.5">
                            <RotateCcw className="mt-0.5 h-4 w-4 shrink-0" />
                            <span><span className="font-bold">Revision requested: </span>{latestRevision.feedback}</span>
                          </p>
                          {latestRevision.revision_notes?.length > 0 && (
                            <div className="mt-3 space-y-1.5 pl-6">
                              {latestRevision.revision_notes.map((n, i) => (
                                <p key={i} className="flex items-start gap-2 text-xs text-rose-200">
                                  <span className="mt-0.5 shrink-0 rounded-full border border-cyan-500/30 bg-cyan-500/15 px-2 py-0.5 font-mono-label text-[10px] font-bold text-cyan-300">{fmtTime(n.t)}</span>
                                  {n.text}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {expanded === j.id && <JobDetails job={j} />}

                      {(j.status === "active" || j.status === "in_revision") && (
                        <SubmitWorkForm job={j} onDone={() => { loadMyJobs(); setTab("submissions"); }} />
                      )}
                      {j.status === "awaiting_review" && (() => {
                        const latest = [...(j.submissions || [])].sort((a, b) => b.version - a.version)[0];
                        const replaceable = latest && ["awaiting_review", "manager_approved"].includes(latest.status);
                        return (
                          <div className="mt-4">
                            <p className="flex items-center gap-2 rounded-2xl bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-xs font-semibold text-amber-300">
                              <Hourglass className="h-4 w-4" /> Your latest version is in review — you'll see the outcome in Submissions.
                            </p>
                            {replaceable && (
                              <>
                                <button
                                  data-testid={`replace-submission-btn-${j.id}`}
                                  onClick={() => setReplaceOpen((cur) => (cur === j.id ? null : j.id))}
                                  className="mt-2.5 flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-300 transition-colors hover:bg-cyan-500/20"
                                >
                                  <UploadCloud className="h-3.5 w-3.5" /> {replaceOpen === j.id ? "Cancel replacement" : "Replace / update submission"}
                                </button>
                                {replaceOpen === j.id && <SubmitWorkForm job={j} onDone={() => { setReplaceOpen(null); loadMyJobs(); setTab("submissions"); }} />}
                              </>
                            )}
                          </div>
                        );
                      })()}
                      <VersionHistory job={j} />
                    </article>
                  );
                })
              )}
            </div>
          )}

          {tab === "submissions" && (
            <div data-testid="worker-submissions-panel" className="space-y-4">
              {revisionSubs.length > 0 && (
                <div className="flex items-center gap-2.5 rounded-3xl border border-rose-500/20 bg-rose-500/10 px-6 py-4 text-sm font-semibold text-rose-300">
                  <RotateCcw className="h-5 w-5" /> {revisionSubs.length} job{revisionSubs.length > 1 ? "s need" : " needs"} revisions — check My Jobs for the notes.
                </div>
              )}
              {!myJobs ? (
                <p data-testid="submissions-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading submissions...</p>
              ) : allSubs.length === 0 ? (
                <EmptyState title="No submissions yet" text="Finished work you submit will be listed here with its review status." testid="submissions-empty" />
              ) : (
                allSubs.map((s) => {
                  const isCurrentVersion = s.version === Math.max(...allSubs.filter((x) => x.job_id === s.job_id).map((x) => x.version));
                  return (
                  <article key={s.id} data-testid={`my-submission-${s.id}`} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2.5">
                          <h3 className="font-display text-base font-semibold text-[var(--pv-text)]">{s.job_title}</h3>
                          <span className="rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-raised)] px-2.5 py-0.5 text-[11px] font-bold text-[var(--pv-text-2)]">v{s.version}</span>
                          {isCurrentVersion && (
                            <span data-testid={`my-submission-current-${s.id}`} className="rounded-full border border-cyan-500/30 bg-cyan-500/15 px-2 py-0.5 text-[9px] font-bold uppercase text-cyan-300">Current</span>
                          )}
                          <span data-testid={`my-submission-status-${s.id}`} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${SUB_STATUS_STYLES[s.status] || SUB_STATUS_STYLES.superseded}`}>{s.status.replace(/_/g, " ")}</span>
                        </div>
                        <p className="mt-1 text-xs text-[var(--pv-text-4)]">
                          {s.filename} · {new Date(s.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </p>
                      </div>
                      <a data-testid={`my-submission-dl-${s.id}`} href={fileUrl(s.file_path)} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-4)] hover:border-cyan-500/30">
                        <Download className="h-3 w-3" /> Download
                      </a>
                    </div>
                    {isVideo(s.filename) && (
                      <div className="mt-4">
                        <VideoPlayer path={s.file_path} filename={s.filename} testid={`my-submission-player-${s.id}`} videoRef={getRef(s.id)} />
                      </div>
                    )}
                    {s.note && <p className="mt-3 rounded-2xl bg-[var(--pv-subtle)] px-4 py-2.5 text-xs text-[var(--pv-text-4)]"><span className="font-bold text-[var(--pv-text-2)]">Your note: </span>{s.note}</p>}
                    {s.feedback && <p className="mt-3 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-2.5 text-xs text-rose-300"><span className="font-bold">Revision notes: </span>{s.feedback}</p>}
                    {s.revision_notes?.length > 0 && (
                      <div data-testid={`my-revision-notes-${s.id}`} className="mt-3 space-y-2">
                        {s.revision_notes.map((n, i) => (
                          <div key={i} className="flex items-start gap-2.5 rounded-xl border border-rose-500/20 bg-rose-500/5 px-3 py-2">
                            <button
                              type="button"
                              data-testid={`my-note-seek-${s.id}-${i}`}
                              onClick={() => seekTo(s.id, n.t)}
                              className="mt-0.5 shrink-0 rounded-full border border-cyan-500/30 bg-cyan-500/15 px-2 py-0.5 font-mono-label text-[10px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/25"
                            >
                              {fmtTime(n.t)}
                            </button>
                            <p className="text-xs leading-relaxed text-rose-200">{n.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                  );
                })
              )}
            </div>
          )}

          {tab === "messages" && <WorkerMessages jobs={myJobs || []} onRead={refreshMsgUnread} preset={msgPreset} onPresetConsumed={() => setMsgPreset(null)} />}
          {tab === "manuals" && manualCount > 0 && <ManualsPage />}

          {tab === "earnings" && (
            <div data-testid="worker-earnings-panel" className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-3xl border border-emerald-500/20 bg-emerald-500/10 p-6 sm:p-8">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400/70">Total earned (paid)</p>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15"><Wallet className="h-4 w-4 text-emerald-400" /></span>
                  </div>
                  <p data-testid="worker-earnings-total" className="mt-2 font-display text-4xl font-extrabold tracking-tight text-emerald-400 sm:text-5xl">${totalPaid.toFixed(2)}</p>
                </div>
                <div className="rounded-3xl border border-amber-500/20 bg-amber-500/10 p-6 sm:p-8">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-amber-400/70">On the way (owed)</p>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15"><Hourglass className="h-4 w-4 text-amber-400" /></span>
                  </div>
                  <p data-testid="worker-earnings-owed" className="mt-2 font-display text-4xl font-extrabold tracking-tight text-amber-400 sm:text-5xl">${totalOwed.toFixed(2)}</p>
                </div>
              </div>
              {!payments ? (
                <p data-testid="earnings-loading" className="py-10 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading payments...</p>
              ) : payments.length === 0 ? (
                <EmptyState title="No payments yet" text="Once a job you delivered is approved, the payment shows up here as owed — and as paid once it's sent to you." testid="earnings-empty" />
              ) : (
                <div className="space-y-3">
                  {payments.map((p) => (
                    <div key={p.id} data-testid={`payment-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5 ">
                      <div>
                        <p className="font-display text-base font-bold text-[var(--pv-text)]">{p.job_title || "Job"}</p>
                        <p className="mt-0.5 text-xs text-[var(--pv-text-4)]">
                          {p.status === "paid" && p.paid_at
                            ? `Paid ${new Date(p.paid_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                            : `Approved ${new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
                          {p.method ? ` · ${p.method}` : ""}
                          {p.payout_method?.label && <span className="ml-1 text-cyan-300/80">via {p.payout_method.label}</span>}
                          {(p.bonus ?? 0) > 0 && <span className="ml-1 text-[var(--pv-text-3)]">(${Number(p.base).toFixed(2)} + ${Number(p.bonus).toFixed(2)} bonus)</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {p.has_proof && (
                          <button
                            data-testid={`worker-view-proof-${p.id}`}
                            onClick={() => setProofFor(p)}
                            className="flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3.5 py-1.5 text-[11px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/20"
                          >
                            <Eye className="h-3.5 w-3.5" /> View proof
                          </button>
                        )}
                        <span className="font-display text-xl font-bold text-[var(--pv-text)]">${p.amount.toFixed(2)}</span>
                        <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${p.status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>{p.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "profile" && (
            <div data-testid="worker-profile-panel" className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-8  sm:p-10">
              <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Profile</h2>
              <div className="mt-6 divide-y divide-[var(--pv-border)] rounded-2xl border border-[var(--pv-border)]">
                {[
                  { label: "Name", value: me.name || "—" },
                  { label: "Email", value: me.email },
                  { label: "Role", value: me.worker_role || me.applied_role || "Crew member" },
                  {
                    label: "Member since",
                    value: me.created_at
                      ? new Date(me.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
                      : "—",
                  },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3.5">
                    <span className="text-sm text-[var(--pv-text-4)]">{row.label}</span>
                    <span className="text-sm font-bold text-[var(--pv-text)]">{row.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6" id="payment-details-anchor">
                <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Payment Details</h3>
                <PaymentDetailsForm />
              </div>
              {!me.demo && (
                <div className="mt-6">
                  <ChangePasswordCard testid="worker-change-password" />
                </div>
              )}
              <div className="mt-6">
                <NotificationPrefsCard />
              </div>
            </div>
          )}
        </div>
          </div>
        </div>
      </main>
      {proofFor && <ProofModal payment={proofFor} onClose={() => setProofFor(null)} />}
    </div>
  );
};
